import React, { useCallback, useEffect, useState, useRef } from 'react';
import { AiOutlineArrowUp, AiOutlineArrowDown } from 'react-icons/ai';

import { ReactComponent as ArrowLeft } from '../../../assets/icons/ubr_arrow_left.svg';
import { ReactComponent as ArrowRight } from '../../../assets/icons/ubr_arrow_right.svg';
import { ReactComponent as Delete } from '../../../assets/icons/ubr_delete.svg';
import { ReactComponent as Edit } from '../../../assets/icons/ubr_edit.svg';
import { Layout, Button, Modal, Shimmer } from '../../../components';
import { useToast } from '../../../hooks/toast';
import useRedirect from '../../../hooks/useRedirect';
import { RoutesPaths } from '../../../routes';
import api from '../../../services/api';
import type {
  InstructionData,
  InstructionDataDTO,
} from '../../../shared/interfaces/instructions';
import PopulateFaqScreens from '../PopulateScreens';
import {
  TableContainer,
  OrderContainer,
  FooterTable,
  PageSize,
  Pagination,
  PaginationButton,
  PaginationItem,
  LoadingData,
  ReactSelectCustom,
} from './stytes';

interface DataProps {
  id: string;
  nomeDaInstrucao: string;
  textoDaInstrucao: string;
  listaDeTelas: [
    {
      id: string;
      titulo: string;
      icone: string;
      subtitulo: string;
      url: string;
    },
  ];
}

const Lista: React.FC = () => {
  const [dataTable, setDataTable] = useState<InstructionData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [idToDelete, setIdToDelete] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const { addToast } = useToast();
  const sortAscending = useRef<boolean>(true);
  const redirect = useRedirect();

  // Pagination

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setitemsPerPage] = useState<number>(5);
  const [pageNumberLimit] = useState<number>(5);
  const [maxPageNumberLimit, setmaxPageNumberLimit] = useState<number>(5);
  const [minPageNumberLimit, setminPageNumberLimit] = useState<number>(0);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get<InstructionDataDTO[]>('/instrucao');

      const instruction = await PopulateFaqScreens(data);
      setDataTable(instruction);

      setIsLoading(false);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const deleteCard = async () => {
    closeModal();
    await api.delete(`/instrucao/${idToDelete}`);

    addToast({
      type: 'success',
      title: 'Instrução excluída com sucesso!',
      description: 'A Instrução foi removida.',
    });

    fetchData();
  };

  const openModal = (id: string) => {
    setIdToDelete(id);
    setShowDeleteModal(true);
  };

  const closeModal = () => {
    setShowDeleteModal(false);
  };

  const sortListManual = (manual: InstructionData[]): InstructionData[] => {
    let orderList = manual.sort(function (a, b): any {
      if (a.nomeDaInstrucao.toUpperCase() < b.nomeDaInstrucao.toUpperCase())
        return -1;
      if (a.nomeDaInstrucao.toUpperCase() > b.nomeDaInstrucao.toUpperCase())
        return 1;
      return 0;
    });
    if (sortAscending.current) {
      return orderList;
    }
    return orderList.reverse();
  };

  const toggleListOrder = () => {
    sortAscending.current = !sortAscending.current;
    const sortedList = sortListManual(dataTable);
    setDataTable([...sortedList]);
  };

  // Renderizar a tabela

  const loadingTable = () => {
    return (
      <tr>
        <td>
          <div
            style={{
              width: '100%',
              height: '40px',
              overflow: 'hidden',
              borderRadius: '4px',
            }}
          >
            <Shimmer />
          </div>
        </td>
        <td>
          <div
            style={{
              width: '100%',
              height: '40px',
              overflow: 'hidden',
              borderRadius: '4px',
            }}
          >
            <Shimmer />
          </div>
        </td>
        <td>
          <div
            style={{
              width: '100%',
              height: '40px',
              overflow: 'hidden',
              borderRadius: '4px',
            }}
          >
            <Shimmer />
          </div>
        </td>
        <td>
          <div
            style={{
              width: '70%',
              height: '40px',
              overflow: 'hidden',
              borderRadius: '4px',
            }}
          >
            <Shimmer />
          </div>
        </td>
      </tr>
    );
  };

  const renderData = (data: DataProps | any) => {
    return (
      <TableContainer>
        <table id="tableData">
          <thead>
            <tr>
              <th style={{ width: '200px' }}>
                <OrderContainer>
                  Nome
                  {sortAscending.current ? (
                    <button onClick={() => toggleListOrder()}>
                      <AiOutlineArrowUp />
                    </button>
                  ) : (
                    <button onClick={() => toggleListOrder()}>
                      <AiOutlineArrowDown />
                    </button>
                  )}
                </OrderContainer>
              </th>
              <th style={{ width: '140px' }}>Texto</th>
              <th style={{ width: '350px' }}>Tela</th>
              <th style={{ width: '140px' }}>Ações</th>
            </tr>
          </thead>
          {isLoading ? (
            <tbody>
              {loadingTable()}
              {loadingTable()}
              {loadingTable()}
            </tbody>
          ) : !data.length ? (
            <LoadingData> Sem dados para exibir... </LoadingData>
          ) : (
            <tbody>
              {data.map((data: any, index: number) => (
                <tr key={index}>
                  <td data-column="Nome">
                    <p>{data.nomeDaInstrucao}</p>
                  </td>
                  <td data-column="Texto">
                    <p className="letterUpercase">
                      {data.textoDaInstrucao.length > 80
                        ? data.textoDaInstrucao.substring(0, 80 - 3) + '...'
                        : data.textoDaInstrucao}

                      {data.textoDaInstrucao.length > 80 && (
                        <span className="tooltip">{data.textoDaInstrucao}</span>
                      )}
                    </p>
                  </td>
                  <td data-column="Tela">
                    <ul>
                      {data && data.listaDeTelas
                        ? data.listaDeTelas.map((screen: any, index: any) => {
                            return <li key={index}>{screen.titulo}</li>;
                          })
                        : ''}
                    </ul>
                  </td>
                  <td data-column="Ações">
                    <div>
                      <Button
                        color={'#ab934b'}
                        variant="solid-icon"
                        onClick={() =>
                          redirect(RoutesPaths.editInstruction + data.id)
                        }
                        tooltipText={'Editar'}
                      >
                        <Edit style={{ width: '20px' }} />
                      </Button>
                      <Button
                        color={'#C71F37'}
                        variant="solid-icon"
                        onClick={() =>
                          setTimeout(() => {
                            openModal(data.id);
                          }, 500)
                        }
                        tooltipText={'Excluir'}
                      >
                        <Delete style={{ width: '20px' }} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </TableContainer>
    );
  };

  // Funções para realização da paginação

  const handleClick = (event: any) => {
    setCurrentPage(Number(event.target.id));
  };

  const pages = [];
  for (let i = 1; i <= Math.ceil(dataTable.length / itemsPerPage); i++) {
    pages.push(i);
  }

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = dataTable.slice(indexOfFirstItem, indexOfLastItem);

  const renderPageNumbers = pages.map((number: any) => {
    if (number < maxPageNumberLimit + 1 && number > minPageNumberLimit) {
      return (
        <PaginationItem
          key={number}
          id={number}
          onClick={handleClick}
          aria-hidden={typeof number !== 'number' ? 'true' : 'false'}
          aria-label={`${
            typeof number !== 'number' ? '' : `Ir para a página ${number}`
          }`}
          aria-current={number === currentPage}
          style={
            currentPage === number
              ? { border: '1px solid #ab934b', color: '#ab934b' }
              : undefined
          }
        >
          {number}
        </PaginationItem>
      );
    } else {
      return null;
    }
  });

  const handleNextbtn = () => {
    setCurrentPage(currentPage + 1);

    if (currentPage + 1 > maxPageNumberLimit) {
      setmaxPageNumberLimit(maxPageNumberLimit + pageNumberLimit);
      setminPageNumberLimit(minPageNumberLimit + pageNumberLimit);
    }
  };

  const handlePrevbtn = () => {
    setCurrentPage(currentPage - 1);

    if ((currentPage - 1) % pageNumberLimit === 0) {
      setmaxPageNumberLimit(maxPageNumberLimit - pageNumberLimit);
      setminPageNumberLimit(minPageNumberLimit - pageNumberLimit);
    }
  };

  let pageIncrementBtn = null;
  if (pages.length > maxPageNumberLimit) {
    pageIncrementBtn = (
      <PaginationItem onClick={handleNextbtn}> &hellip; </PaginationItem>
    );
  }

  let pageDecrementBtn = null;
  if (minPageNumberLimit >= 1) {
    pageDecrementBtn = (
      <PaginationItem onClick={handlePrevbtn}> &hellip; </PaginationItem>
    );
  }

  const limits = useCallback(e => {
    setitemsPerPage(e.value);
    setCurrentPage(1);
  }, []);

  const options = [
    { value: 5, label: 5 },
    { value: 10, label: 10 },
    { value: 20, label: 20 },
    { value: 50, label: 50 },
  ];

  // =========================================================================================

  return (
    <>
      <Modal
        show={showDeleteModal}
        titulo={'Excluir Instrução'}
        mensagem={'Confirma a exclusão da Instrução?'}
        onConfirm={() => deleteCard()}
        onCancel={() => closeModal()}
        actionButtons
      />
      <Layout
        title={'Lista de Instruções'}
        buttonTitle={'Nova Instrução'}
        buttonRedirect={RoutesPaths.registerInstruction}
      >
        <TableContainer>
          {renderData(currentItems)}
          <FooterTable>
            <PageSize>
              Dados por página
              <ReactSelectCustom
                classNamePrefix="react-select"
                onChange={limits}
                options={options}
                defaultValue={itemsPerPage}
                placeholder={5}
                isSearchable={false}
              />
            </PageSize>
            <Pagination>
              <PaginationButton aria-label="Paginação">
                <PaginationItem
                  onClick={handlePrevbtn}
                  disabled={
                    currentPage === pages[0]
                      ? true
                      : false || pages.length <= 1
                      ? true
                      : false
                  }
                  aria-label={`Voltar ${
                    currentPage === 1
                      ? 'página'
                      : `para página ${currentPage - 1}`
                  }`}
                  tabIndex={currentPage === pages[pages.length - 1] ? -1 : 0}
                  aria-disabled={
                    currentPage === pages[pages.length - 1] ? 'true' : 'false'
                  }
                >
                  <ArrowLeft />
                </PaginationItem>
                {pageDecrementBtn}
                {renderPageNumbers}
                {pageIncrementBtn}
                <PaginationItem
                  onClick={handleNextbtn}
                  disabled={
                    currentPage === pages[pages.length - 1]
                      ? true
                      : false || pages.length <= 1
                      ? true
                      : false
                  }
                  aria-label={`Avançar ${
                    currentPage === pages[pages.length - 1]
                      ? 'página'
                      : `para página ${currentPage + 1}`
                  }`}
                  tabIndex={currentPage === pages[pages.length - 1] ? -1 : 0}
                  aria-disabled={
                    currentPage === pages[pages.length - 1] ? 'true' : 'false'
                  }
                >
                  <ArrowRight />
                </PaginationItem>
              </PaginationButton>
            </Pagination>
          </FooterTable>
        </TableContainer>
      </Layout>
    </>
  );
};

export default Lista;
